import { useCallback, useState } from 'react';

import {
  WorkspacesPageListDocument,
  useWorkspacesPageCreateMutation,
} from '../../../generated';
import type { Workspace } from '../types';
import { mapToWorkspaces } from '../utils';

export interface CreateWorkspaceInput {
  name: string;
  rootLocationId: string;
}

export interface UseWorkspaceCreateResult {
  createWorkspace: (input: CreateWorkspaceInput) => Promise<Workspace | null>;
  isCreating: boolean;
  error: string | null;
  clearError: () => void;
}

export const useWorkspaceCreate = (): UseWorkspaceCreateResult => {
  const [error, setError] = useState<string | null>(null);
  const [mutate, { loading }] = useWorkspacesPageCreateMutation({
    // The new workspace has to appear in the list behind the form.
    refetchQueries: [{ query: WorkspacesPageListDocument }],
  });

  const createWorkspace = useCallback(
    async (input: CreateWorkspaceInput): Promise<Workspace | null> => {
      setError(null);

      try {
        const { data } = await mutate({ variables: { input } });
        const result = data?.workspaceCreate;

        switch (result?.__typename) {
          case 'WorkspaceCreateResult':
            return mapToWorkspaces([result.workspace])[0] ?? null;

          // Each failure is its own type, so the message can name the offending
          // root rather than saying something went wrong.
          case 'WorkspaceRootLocationAlreadyClaimed':
            setError(
              `Another workspace already holds ${result.rootLocation.id} as a root.`
            );
            return null;

          // The distributor sits under another one, so a workspace rooted
          // here would leave everything above it outside.
          case 'WorkspaceRootLocationNotAHierarchyRoot':
            setError(
              'That distributor sits under another one. Pick the one at the top instead.'
            );
            return null;

          case 'WorkspaceRootLocationNotFound':
            setError('That distributor could not be found.');
            return null;

          case 'InputValidationFailure':
            setError(result.message ?? 'The workspace details are not valid.');
            return null;

          case 'AuthorizationFailure':
            setError(result.message ?? 'Not authorized to create a workspace.');
            return null;

          default:
            setError('The workspace could not be created.');
            return null;
        }
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : 'The workspace could not be created.'
        );
        return null;
      }
    },
    [mutate]
  );

  return {
    createWorkspace,
    isCreating: loading,
    error,
    clearError: () => setError(null),
  };
};
